import React, { useState, useEffect } from 'react';
import { Upload, X, AlertTriangle } from 'lucide-react';
import { PrimaryButton, OutlineButton } from './Buttons';
import axiosInstance from '../services/api/axios';
import { toast } from 'react-toastify';

import { getPresignedUploadUrls, uploadFilesToS3 } from '../utils/fileUtils';
import BoqContainer from '../pages/BOQ/BoqContainer';
import { HOUSE_SECTIONS } from '../constants/houseSections';
import { FormModal } from './form';
import { hasStatus, ID_DELIVERY_EXPIRED } from '../utils/idLeadStatus';

// AutoCAD / CAD file extensions and MIME hints
const AUTOCAD_ACCEPT = '.dwg,.dxf,application/acad,application/dxf,image/vnd.dwg';

const DesignSubmissionModal = ({
  isOpen,
  onClose,
  configId,
  leadId,
  onSuccess,
  editingDesign = null,
  editingIterationId = null, // when editing BOQ, pass the selected iteration's ID for BOQ scope
  iterationSubmission = null, // { designSubmissionId?, designIterationId?, title, description } -> create a new iteration for existing context
  startAtStep = 0,
  portfolioId,
  isFinalForNewIteration = false, // when true, new iteration is sent with is_final: true (e.g. previous iteration had isFinal)
  previousEstimatedPrice, // when isNewIteration, use this for API (no field shown)
  initialSubmissionDraft = null, // optional draft for first submit (featured lead seed)
  lead = null, // optional ID lead — used to warn when design delivery deadline has expired
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [activeStep, setActiveStep] = useState(startAtStep); // 0 = Design Submission, 1 = BOQ
  const [designSubmissionId, setDesignSubmissionId] = useState(null);
  const [designIterationId, setDesignIterationId] = useState(null);
  const [designForm, setDesignForm] = useState({
    title: '',
    description: '',
    estimatedPrice: '',
    designerNotes: '',
    images: [],
  });
  // Per-field validation errors — keyed by field name. Surfaced inline as
  // red helper text under each field, in addition to the existing toast.
  const [fieldErrors, setFieldErrors] = useState({});
  const clearFieldError = (name) =>
    setFieldErrors((prev) => {
      if (!(name in prev)) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  const [selectedFiles, setSelectedFiles] = useState([]); // [{ file, section, preview }, ...]
  const [selectedAutocadFiles, setSelectedAutocadFiles] = useState([]); // [{ file }, ...]
  const [existingImages, setExistingImages] = useState([]);
  const [seedMedia, setSeedMedia] = useState([]);

  // When isFinal=true the design submission is held in this buffer at
  // step 0 instead of being POSTed straight away. The BOQ Save click
  // then POSTs the design + BOQ atomically (with DELETE-rollback if the
  // BOQ step fails after the design was created). Shape:
  //   { kind: 'new-submission' | 'new-iteration', body, ... }
  const [pendingSubmission, setPendingSubmission] = useState(null);

  const isNewIteration = Boolean(iterationSubmission && !editingDesign);
  // BOQ is taken (and therefore the atomic-save flow is used) only when
  // the iteration being created is final. Non-final iterations and edits
  // commit immediately at step 0 as before.

  // Reset form when modal closes; revoke preview URLs
  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles((prev) => {
        prev.forEach((entry) => { if (entry?.preview) URL.revokeObjectURL(entry.preview); });
        return [];
      });
      setDesignForm({
        title: '',
        description: '',
        estimatedPrice: '',
        designerNotes: '',
        images: [],
      });
      setSelectedAutocadFiles([]);
      setExistingImages([]);
      setSeedMedia([]);
      setPendingSubmission(null);
      setActiveStep(startAtStep);
      setDesignSubmissionId(null);
      setDesignIterationId(null);
    } else if (editingDesign) {
      // Load editing design data
      const subId = editingDesign.id || editingDesign.designSubmissionId;
      setDesignSubmissionId(subId);
      const iterId = editingIterationId ?? editingDesign.designIterationId ?? editingDesign.designIterations?.[0]?.designIterationId ?? editingDesign.designIterations?.[0]?.id;
      setDesignIterationId(iterId || null);
      setDesignForm({
        title: editingDesign.title || '',
        description: editingDesign.description || '',
        estimatedPrice: editingDesign.estimatedPrice?.toString() || '',
        designerNotes: editingDesign.designerNotes || '',
        images: [],
      });
      setExistingImages(editingDesign.images || []);
      setSeedMedia([]);
      setSelectedFiles([]);
      setSelectedAutocadFiles([]);
      setActiveStep(startAtStep);
    } else if (iterationSubmission) {
      // Create new iteration for an existing submission.
      // Title becomes the iteration's own name (editable, blank by default so the
      // designer can describe what's new in this iteration). Description is a
      // submission-level field and is not collected per iteration. Estimated
      // price is pre-filled from the previous iteration so the designer can
      // adjust it instead of re-entering from scratch.
      const subId = iterationSubmission.designSubmissionId || iterationSubmission.id || iterationSubmission.designSubmission?.designSubmissionId;
      const iterId = iterationSubmission.designIterationId || iterationSubmission.design_iteration_id || null;
      setDesignSubmissionId(subId || null);
      setDesignIterationId(iterId || null);
      setDesignForm({
        title: '',
        description: iterationSubmission.description || '',
        estimatedPrice:
          previousEstimatedPrice !== undefined &&
          previousEstimatedPrice !== null &&
          String(previousEstimatedPrice).trim() !== ''
            ? String(previousEstimatedPrice)
            : '',
        designerNotes: '',
        images: [],
      });
      setExistingImages([]);
      setSeedMedia([]);
      setSelectedFiles([]);
      setSelectedAutocadFiles([]);
      setActiveStep(startAtStep);
    } else if (isOpen && initialSubmissionDraft) {
      const draftMedia = Array.isArray(initialSubmissionDraft.media) ? initialSubmissionDraft.media : [];
      setDesignForm({
        title: initialSubmissionDraft.title || '',
        description: initialSubmissionDraft.description || '',
        estimatedPrice:
          initialSubmissionDraft.estimatedPrice !== undefined &&
          initialSubmissionDraft.estimatedPrice !== null
            ? String(initialSubmissionDraft.estimatedPrice)
            : '',
        designerNotes: initialSubmissionDraft.designerNotes || '',
        images: [],
      });
      setExistingImages([]);
      setSeedMedia(draftMedia);
      setSelectedFiles([]);
      setSelectedAutocadFiles([]);
      setActiveStep(startAtStep);
    }
  }, [isOpen, editingDesign, editingIterationId, iterationSubmission, initialSubmissionDraft, startAtStep, previousEstimatedPrice]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const newEntries = files.map((file) => ({
      file,
      section: HOUSE_SECTIONS[0],
      preview: URL.createObjectURL(file),
    }));
    setSelectedFiles((prev) => [...prev, ...newEntries]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => {
      const entry = prev[index];
      if (entry?.preview) URL.revokeObjectURL(entry.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleImageSectionChange = (index, section) => {
    setSelectedFiles((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, section } : entry))
    );
  };

  const handleAutocadFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setSelectedAutocadFiles((prev) => [...prev, ...files.map((f) => ({ file: f }))]);
  };

  const removeAutocadFile = (index) => {
    setSelectedAutocadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Convert file to base64 (used only for edit flow when backend expects media string)
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  /**
   * Upload selected files to S3 and return array of media objects with key and content_type.
   * Uses presigned upload URL flow: get URLs -> upload to S3 -> return media objects.
   */
  const uploadDesignImagesToS3 = async (files) => {
    if (!files || files.length === 0) return [];
    // Send the logical folder name; backend expands this to the
    // scoped interior-designer S3 prefix before signing the PUT URL.
    const folder = 'designs';
    const presignedData = await getPresignedUploadUrls(files, folder, axiosInstance);
    const uploadData = presignedData.map((presigned, index) => ({
      file: files[index],
      upload_url: presigned.upload_url,
      content_type: presigned.content_type,
    }));
    await uploadFilesToS3(uploadData);
    // Return media objects with s3_key and content_type (API expects s3_key)
    return presignedData.map((presigned, index) => ({
      s3_key: presigned.s3_key,
      content_type: presigned.content_type || files[index].type,
    }));
  };
  const handleNext = async () => {
    if (activeStep === 0) {
      // Build a per-field error map so the user sees red helper text under
      // each problem field — not just a transient toast. handleNext also
      // surfaces a top-level toast for the first issue so the user notices
      // the validation triggered.
      const errs = {};
      if (!designForm.title.trim()) {
        errs.title = 'Please enter a design title';
      } else if (designForm.title.trim().length < 3) {
        errs.title = 'Title is too short — use at least 3 characters';
      } else if (designForm.title.trim().length > 100) {
        errs.title = 'Title is too long — keep it under 100 characters';
      }

      if (
        designForm.estimatedPrice !== '' &&
        designForm.estimatedPrice !== null &&
        designForm.estimatedPrice !== undefined
      ) {
        const priceNum = Number(designForm.estimatedPrice);
        if (Number.isNaN(priceNum)) {
          errs.estimatedPrice = 'Estimated price must be a number';
        } else if (priceNum < 0) {
          errs.estimatedPrice = 'Estimated price cannot be negative';
        }
      }

      const hasSeedMedia = !editingDesign && !isNewIteration && seedMedia.length > 0;
      const hasImages = selectedFiles.length > 0 || existingImages.length > 0 || hasSeedMedia;
      // Design images are mandatory for every submission/iteration (an AutoCAD
      // file alone is not enough).
      if (!hasImages) {
        errs.images = 'Please upload at least one design image';
      }

      if (!leadId && !editingDesign) {
        errs.leadId = 'Lead ID is required';
      }

      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
        const firstError = errs.title || errs.estimatedPrice || errs.images || errs.leadId;
        if (firstError) toast.error(firstError);
        return;
      }
      // Clear any leftover errors from a previous failed attempt.
      setFieldErrors({});

      setSubmitting(true);
      try {
        if (editingDesign) {
          // Submitted designs are append-only deliverables now — there is no edit endpoint.
          // Instead of editing, the designer submits a new version from the lead screen.
          toast.info('Submitted designs cannot be edited. Please upload a new version instead.');
          setSubmitting(false);
          return;
        }

        // New submission or new iteration: upload images and autocad to S3, then send media array with section per item
        const imageFiles = selectedFiles.map((e) => e.file);
        const autocadFiles = selectedAutocadFiles.map((e) => (typeof e === 'object' && e?.file ? e.file : e));
        const rawImageMedia = imageFiles.length > 0 ? await uploadDesignImagesToS3(imageFiles) : [];
        const rawAutocadMedia = autocadFiles.length > 0 ? await uploadDesignImagesToS3(autocadFiles) : [];
        const mediaArray = [
          ...rawImageMedia.map((m, i) => ({
            s3_key: m.s3_key || m.key,
            content_type: m.content_type,
            section: selectedFiles[i]?.section || HOUSE_SECTIONS[0],
          })),
          ...rawAutocadMedia.map((m) => ({
            s3_key: m.s3_key || m.key,
            content_type: m.content_type,
            section: 'Other',
          })),
        ];
        if (!isNewIteration && mediaArray.length === 0 && seedMedia.length === 0) {
          toast.error('Please upload at least one design image or AutoCAD file');
          setSubmitting(false);
          return;
        }

        // The lead id — every design submission is a new deliverable on the lead now.
        const targetLeadId =
          leadId ||
          iterationSubmission?.idLeadId ||
          iterationSubmission?.designSubmission?.idLead?.interiorDesignerLeadId ||
          iterationSubmission?.interiorDesignerLeadId;

        if (isNewIteration) {
          if (!targetLeadId) {
            throw new Error('Lead context is required to submit a new design');
          }
          // Prefer the user-edited estimated price; fall back to the previous
          // deliverable's price if the field is left blank.
          const editedPriceNum = parseFloat(designForm.estimatedPrice);
          const fallbackPriceNum = parseFloat(previousEstimatedPrice);
          const estimatedPrice = Number.isFinite(editedPriceNum) && editedPriceNum > 0
            ? editedPriceNum
            : Number.isFinite(fallbackPriceNum) && fallbackPriceNum > 0
              ? fallbackPriceNum
              : 0;

          // RequestSubmitDesign shape.
          const requestBody = {
            iterationName: designForm.title?.trim() || '',
            estimatedPrice,
            media: mediaArray,
            designerNotes: designForm.designerNotes?.trim() || '',
            isFinal: Boolean(isFinalForNewIteration),
          };
          const submitEndpoint = `/interior-designers/leads/${targetLeadId}/design`;

          // Atomic flow: when the design is final we buffer the payload (media already
          // uploaded to S3 above) and navigate to BOQ; the BOQ Save click POSTs the
          // deliverable + BOQ rows together, with rollback if BOQ fails.
          if (isFinalForNewIteration) {
            setPendingSubmission({
              kind: 'new-iteration',
              endpoint: submitEndpoint,
              body: requestBody,
            });
            setActiveStep(1);
            return;
          }

          await axiosInstance.post(submitEndpoint, requestBody, {
            headers: { 'Content-Type': 'application/json' },
          });
          toast.success('Updated design uploaded successfully!');
          if (onSuccess) onSuccess();
          onClose();
          return;
        }
       

        // Create new design submission with iteration (media array)
        const seededMediaArray = (seedMedia || []).map((item) => {
          if (typeof item === "string") {
            const isBase64ish =
              item.startsWith("data:") || item.startsWith("/9j/") || item.startsWith("iVBORw0KGgo");
            const isUrl = item.startsWith("http://") || item.startsWith("https://");
            if (isBase64ish || isUrl) return null;
            return { s3_key: item, section: HOUSE_SECTIONS[0] };
          }
          return {
            s3_key: item?.s3_key || item?.s3Key || item?.key || item?.media_key || item?.mediaKey,
            content_type: item?.content_type || item?.contentType,
            section: item?.section || HOUSE_SECTIONS[0],
          };
        }).filter((item) => Boolean(item?.s3_key));

        if (!targetLeadId) {
          throw new Error('Lead context is required to submit a design');
        }
        // RequestSubmitDesign — the first design of a lead (isFinal=false → skip BOQ step).
        const requestBody = {
          iterationName: '',
          estimatedPrice: parseFloat(designForm.estimatedPrice) || 0,
          media: [...seededMediaArray, ...mediaArray],
          designerNotes: designForm.designerNotes?.trim() || '',
          isFinal: false,
        };

        const response = await axiosInstance.post(
          `/interior-designers/leads/${targetLeadId}/design`,
          requestBody,
          { headers: { 'Content-Type': 'application/json' } }
        );

        const newDesignIterationId = response?.designIterationId || response?.id;
        setDesignIterationId(newDesignIterationId || null);
        toast.success('Design submitted successfully!');
        if (onSuccess) onSuccess();
        onClose();
        return;
      } catch (error) {
        console.error('Error creating/updating design submission:', error);
        if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (Array.isArray(detail)) {
            const errorMessages = detail.map((err) => err.msg || err.message || JSON.stringify(err)).join(', ');
            toast.error(errorMessages);
          } else {
            toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
          }
        } else {
          toast.error(
            editingDesign
              ? 'Failed to update design submission. Please try again.'
              : isNewIteration
                ? 'Failed to upload updated design. Please try again.'
                : 'Failed to create design submission. Please try again.'
          );
        }
      } finally {
        setSubmitting(false);
      }
    }
  };

  // Atomic-flow callback handed to BoqContainer. Called at the start of
  // the BOQ Save click — POSTs the deferred design iteration first and
  // returns the freshly-minted IDs that BoqContainer should attach the
  // BOQ rows to. Returns null when there is no pending submission (the
  // BOQ-edit-only flow, e.g. clicking "Edit BOQ" on an existing
  // iteration), so BoqContainer falls through to its prop-derived IDs.
  const handleBoqPreSave = async () => {
    if (!pendingSubmission) return null;
    const iterResponse = await axiosInstance.post(
      pendingSubmission.endpoint,
      pendingSubmission.body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    // Tolerate every response shape: axios may hand back the data object
    // directly (our interceptor) or the full response; the iteration id may
    // be camel or snake. Fall back to the parent iteration only as a last
    // resort.
    const iterData = iterResponse?.data ?? iterResponse ?? {};
    // The submit endpoint returns a ResponseDesignDeliverable carrying its id.
    const newIterationId = iterData?.designIterationId ?? iterData?.id ?? null;
    if (!newIterationId) {
      throw new Error(
        'The design was saved but its ID was not returned, so the BOQ could not be attached. Please try again.',
      );
    }
    setDesignIterationId(newIterationId);
    // Clear the buffer — the deliverable is now persisted. If the BOQ POST that
    // follows fails, handleBoqSaveError will undo it.
    setPendingSubmission(null);
    return {
      designSubmissionId: null,
      designIterationId: newIterationId,
    };
  };

  // Rollback: BOQ POST failed after handleBoqPreSave created the
  // iteration. Delete it so we don't leave a half-committed iteration
  // and re-populate pendingSubmission so the designer can retry without
  // losing their work (their media is still uploaded; they can edit and
  // re-submit).
  const handleBoqSaveError = async ({ designIterationId: createdIterationId }) => {
    // Deliverables are append-only (no delete endpoint). The design is persisted; only the
    // BOQ attach failed — keep the deliverable and let the designer retry the BOQ.
    if (!createdIterationId) return;
    setDesignIterationId(createdIterationId);
    toast.error('Design was submitted but the BOQ failed to save. Please edit the BOQ and try again.');
  };

  // Title + subtitle depend on which step the wizard is on. Step 0 is the
  // narrower "design details" view (max-w-2xl ≈ lg); step 1 is the full-width
  // BOQ editor (max-w-[95%] ≈ full). FormModal can't dynamically toggle size
  // — we pass `2xl` for step 0 and `full` for the BOQ step.
  const titleText = activeStep === 0
    ? (editingDesign ? 'Edit Design Submission' : isNewIteration ? 'Upload Updated Design' : 'Create Design Submission')
    : 'Bill of Quantities (BOQ)';
  const subtitleText = activeStep === 0
    ? (editingDesign
        ? 'Update your interior design concept'
        : isNewIteration
          ? 'Upload a revised design after implementing requested modifications'
          : 'Submit your interior design concept for this lead')
    : 'Add detailed BOQ for your design submission';

  return (
    <FormModal
      open={isOpen}
      onClose={submitting ? () => {} : onClose}
      title={titleText}
      size={activeStep === 1 ? 'full' : 'lg'}
    >
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-4">{subtitleText}</p>

          {/* Delivery-expired warning — only when creating a new iteration on a lead
              whose design delivery window has elapsed. Surfaces but does not block. */}
          {isNewIteration && lead && hasStatus(lead, ID_DELIVERY_EXPIRED) && (
            <div className="mx-6 mb-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" aria-hidden />
              <span>
                This lead&apos;s delivery window expired. New iterations may not be accepted — confirm with the buyer
                before submitting.
              </span>
            </div>
          )}

          {/* Step Indicator / Tabs.
              When the modal was opened from "Edit BOQ" (editingDesign +
              startAtStep===1), the designer has explicitly asked for the
              BOQ step only — surfacing the Design Details tab there lets
              them land on a half-loaded edit form whose Update Design
              button is disabled because existingImages didn't hydrate
              from a design submission (images live on iterations, not
              submissions). Hide the indicator entirely in that mode. */}
          {!(editingDesign && startAtStep === 1) && (
          <div className="px-6 py-2 border-b border-gray-200 bg-gray-50 shrink-0">
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => setActiveStep(0)}
                className={`flex items-center transition-colors ${activeStep === 0 ? 'text-flamingo-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${activeStep >= 0 ? 'bg-flamingo-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                  {(designSubmissionId || designIterationId) ? '✓' : '1'}
                </div>
                <span className="ml-2 text-sm font-bold">Design Details</span>
              </button>
              <div className={`w-12 h-0.5 ${activeStep >= 1 ? 'bg-flamingo-600' : 'bg-gray-300'}`} />
              {/* BOQ is gated on iterations marked as final. Sample (non-final)
                  iterations don't get a BOQ — materials are priced against the
                  accepted design, not exploratory mocks. */}
              {(() => {
                const editingIterationIsFinal = Boolean(
                  editingDesign?.rowIterationIsFinal ??
                    editingDesign?._iteration?.isFinal ??
                    editingDesign?._iteration?.is_final ??
                    isFinalForNewIteration
                );
                const boqEnabled = Boolean(designSubmissionId || designIterationId) && editingIterationIsFinal;
                const boqTitle = editingIterationIsFinal
                  ? undefined
                  : 'BOQ is available only on iterations marked as final';
                return (
                  <button
                    type="button"
                    disabled={!boqEnabled}
                    title={boqTitle}
                    onClick={() => boqEnabled && setActiveStep(1)}
                    className={`flex items-center transition-colors ${
                      activeStep === 1
                        ? 'text-flamingo-600'
                        : boqEnabled
                          ? 'text-gray-400 hover:text-gray-600'
                          : 'text-gray-200 cursor-not-allowed'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${activeStep >= 1 ? 'bg-flamingo-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                      2
                    </div>
                    <span className="ml-2 text-sm font-bold">BOQ</span>
                  </button>
                );
              })()}
            </div>
          </div>
          )}

          {/* Modal Content */}
          <div className="flex-grow overflow-y-auto">
            {activeStep === 0 ? (
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isNewIteration ? 'Iteration Title' : 'Design Title'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={designForm.title}
                    onChange={(e) => {
                      setDesignForm((prev) => ({ ...prev, title: e.target.value }));
                      clearFieldError('title');
                    }}
                    placeholder={
                      isNewIteration
                        ? 'e.g., Revised kitchen layout v2'
                        : 'e.g., Modern Living Room Concept'
                    }
                    maxLength={100}
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 ${
                      fieldErrors.title
                        ? 'border-rose-400 focus:ring-rose-200 focus:border-rose-500'
                        : 'border-gray-300 focus:ring-flamingo-500 focus:border-flamingo-500'
                    }`}
                  />
                  {fieldErrors.title && (
                    <p className="mt-1 text-xs text-rose-600">{fieldErrors.title}</p>
                  )}
                  {!fieldErrors.title && isNewIteration && (
                    <p className="mt-1 text-xs text-gray-500">
                      This becomes the name of this iteration. Description stays from the original design submission.
                    </p>
                  )}
                </div>

                {/* Description is a submission-level field — hide it on follow-up iterations */}
                {!isNewIteration && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={designForm.description}
                      onChange={(e) => setDesignForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe your design concept..."
                      rows={3}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-flamingo-500 focus:border-flamingo-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Price (₹)
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="1"
                    value={designForm.estimatedPrice}
                    onChange={(e) => {
                      setDesignForm((prev) => ({
                        ...prev,
                        estimatedPrice: e.target.value.replace(/^0+/, ''), // No leading zeros
                      }));
                      clearFieldError('estimatedPrice');
                    }}
                    placeholder={
                      isNewIteration
                        ? 'Adjust price for this iteration'
                        : 'Enter an estimated price'
                    }
                    className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-2 ${
                      fieldErrors.estimatedPrice
                        ? 'border-rose-400 focus:ring-rose-200 focus:border-rose-500'
                        : 'border-gray-300 focus:ring-flamingo-500 focus:border-flamingo-500'
                    }`}
                  />
                  {fieldErrors.estimatedPrice && (
                    <p className="mt-1 text-xs text-rose-600">{fieldErrors.estimatedPrice}</p>
                  )}
                  {!fieldErrors.estimatedPrice && isNewIteration && (
                    <p className="mt-1 text-xs text-gray-500">
                      Pre-filled from the previous iteration. Update it if scope or BOQ has changed.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Designer Notes</label>
                  <textarea
                    value={designForm.designerNotes}
                    onChange={(e) => setDesignForm(prev => ({ ...prev, designerNotes: e.target.value }))}
                    placeholder="Add any additional notes..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-flamingo-500 focus:border-flamingo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Design Images <span className="text-red-500">*</span>
                  </label>
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 transition-colors ${
                      fieldErrors.images
                        ? 'border-rose-400 bg-rose-50/40'
                        : 'border-gray-300'
                    }`}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        handleFileSelect(e);
                        clearFieldError('images');
                      }}
                      className="hidden"
                      id="design-images-upload"
                      disabled={submitting}
                    />
                    <label
                      htmlFor="design-images-upload"
                      className="cursor-pointer flex flex-col items-center justify-center"
                    >
                      <Upload className={`${fieldErrors.images ? 'text-rose-400' : 'text-gray-400'} mb-2`} size={32} />
                      <span className="text-sm text-gray-600 font-medium">Click to upload or drag and drop</span>
                      <span className="text-xs text-gray-400 mt-1">At least 1 image required</span>
                    </label>
                  </div>
                  {fieldErrors.images && (
                    <p className="mt-1 text-xs text-rose-600">{fieldErrors.images}</p>
                  )}

                  {/* Existing images from editing */}
                  {existingImages.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs text-gray-500 mb-2">Existing Images:</p>
                      <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3">
                        {existingImages.map((image, index) => {
                          const imageSrc = typeof image === 'string'
                            ? (image.startsWith('/9j/') ? `data:image/jpeg;base64,${image}` : image.startsWith('data:') ? image : `data:image/jpeg;base64,${image}`)
                            : null;
                          return imageSrc ? (
                            <div key={`existing-${index}`} className="relative group">
                              <img
                                src={imageSrc}
                                alt={`Existing ${index + 1}`}
                                className="w-full h-32 object-cover rounded border border-gray-200"
                              />
                              <p className="text-xs text-gray-500 mt-1 truncate">Existing Image {index + 1}</p>
                            </div>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Newly selected files with section tagging.
                      Images are grouped by the room they're tagged for, so the
                      designer can see at a glance which rooms have coverage. */}
                  {selectedFiles.length > 0 && (
                    <div className="mt-4 space-y-4">
                      {existingImages.length > 0 && <p className="text-xs text-gray-500">New Images:</p>}
                      <div className="rounded-lg border border-flamingo-100 bg-flamingo-50/40 p-3">
                        <p className="text-xs font-medium text-flamingo-700">Tag each image by room</p>
                        <p className="text-[11px] text-flamingo-600/80 mt-0.5">
                          Hall, Master Bedroom, Bathroom, Kitchen and so on. Tags drive the grouped preview below and the gallery the buyer sees.
                        </p>
                      </div>
                      {(() => {
                        const grouped = selectedFiles.reduce((acc, entry, i) => {
                          const key = entry.section || HOUSE_SECTIONS[0];
                          (acc[key] = acc[key] || []).push({ entry, index: i });
                          return acc;
                        }, {});
                        const orderedSections = HOUSE_SECTIONS.filter((s) => grouped[s]?.length);
                        return orderedSections.map((sectionName) => (
                          <div key={sectionName}>
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="text-sm font-semibold text-slate-800">{sectionName}</h4>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium">
                                {grouped[sectionName].length}
                              </span>
                            </div>
                            <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3">
                              {grouped[sectionName].map(({ entry, index }) => (
                                <div key={index} className="relative group border border-gray-200 rounded-lg p-2 bg-white">
                                  <div className="relative">
                                    <img
                                      src={entry.preview}
                                      alt={entry.file.name}
                                      className="w-full h-32 object-cover rounded border border-gray-200"
                                    />
                                    <span className="absolute top-1.5 left-1.5 inline-flex items-center px-2 py-0.5 rounded-full bg-slate-900/75 text-white text-[10px] font-medium backdrop-blur-sm">
                                      {entry.section || HOUSE_SECTIONS[0]}
                                    </span>
                                  </div>
                                  <div className="mt-2">
                                    <label className="block text-xs font-medium text-slate-600 mb-1">Room</label>
                                    <select
                                      value={entry.section}
                                      onChange={(e) => handleImageSectionChange(index, e.target.value)}
                                      disabled={submitting}
                                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-flamingo-500 focus:border-flamingo-500"
                                    >
                                      {HOUSE_SECTIONS.map((sec) => (
                                        <option key={sec} value={sec}>{sec}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <button
                                    onClick={() => removeFile(index)}
                                    disabled={submitting}
                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                                  >
                                    <X size={14} />
                                  </button>
                                  <p className="text-[11px] text-gray-500 mt-1 truncate" title={entry.file.name}>
                                    {entry.file.name}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>

                {/* AutoCAD / CAD files upload (optional, all flows) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    AutoCAD / CAD Files <span className="text-gray-500 text-xs">(optional, .dwg, .dxf)</span>
                  </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                      <input
                        type="file"
                        accept={AUTOCAD_ACCEPT}
                        multiple
                        onChange={handleAutocadFileSelect}
                        className="hidden"
                        id="autocad-files-upload"
                        disabled={submitting}
                      />
                      <label
                        htmlFor="autocad-files-upload"
                        className="cursor-pointer flex flex-col items-center justify-center"
                      >
                        <Upload className="text-gray-400 mb-2" size={32} />
                        <span className="text-sm text-gray-600 font-medium">Click to upload AutoCAD / CAD files</span>
                        <span className="text-xs text-gray-400 mt-1">.dwg, .dxf — added to the same media as design images</span>
                      </label>
                    </div>
                    {selectedAutocadFiles.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-500 mb-2">Selected CAD files:</p>
                        <ul className="space-y-1">
                          {selectedAutocadFiles.map((entry, index) => (
                            <li key={index} className="flex items-center justify-between text-sm py-1.5 px-2 rounded bg-gray-50 border border-gray-200">
                              <span className="truncate text-gray-700">{(entry?.file || entry)?.name}</span>
                              <button
                                type="button"
                                onClick={() => removeAutocadFile(index)}
                                disabled={submitting}
                                className="flex-shrink-0 ml-2 p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                                aria-label="Remove file"
                              >
                                <X size={16} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
              </div>
            ) : (
              /* BOQ Step – compact full-height layout.
                 In atomic mode (`pendingSubmission` is set) the BOQ Save
                 click also creates the deferred iteration. We pass the
                 pre-save hook + rollback handler + relabel the button so
                 the affordance reads "Save Design + BOQ". */
              <div className="p-4 lg:p-5 h-full flex flex-col min-h-0">
                <BoqContainer
                  designSubmissionId={designSubmissionId}
                  designIterationId={designIterationId}
                  leadId={leadId}
                  portfolioId={portfolioId}
                  onSuccess={() => {
                    if (onSuccess) onSuccess();
                    onClose();
                  }}
                  onCancel={onClose}
                  hidePriceColumns
                  onPreSave={pendingSubmission ? handleBoqPreSave : undefined}
                  onSaveError={pendingSubmission ? handleBoqSaveError : undefined}
                  saveButtonLabel={pendingSubmission ? 'Save Design + BOQ' : 'Save BOQ'}
                />
              </div>
            )}
          </div>

          {/* Modal Footer (Only for Step 0) */}
          {activeStep === 0 && (
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3 shrink-0">
              <OutlineButton
                onClick={onClose}
                disabled={submitting}
                className="px-6 py-2"
              >
                Cancel
              </OutlineButton>
              <PrimaryButton
                onClick={handleNext}
                disabled={
                  submitting ||
                  (editingDesign && selectedFiles.length === 0 && existingImages.length === 0 && selectedAutocadFiles.length === 0) ||
                  (!editingDesign && !isNewIteration && selectedFiles.length === 0 && selectedAutocadFiles.length === 0 && seedMedia.length === 0) ||
                  (isNewIteration && selectedFiles.length === 0 && selectedAutocadFiles.length === 0)
                }
                className="px-6 py-2 flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  editingDesign
                    ? 'Update Design'
                    : isNewIteration
                      // Atomic flow: final iterations don't save here —
                      // the design + BOQ are committed together by the
                      // BOQ step's Save button. The step 0 click only
                      // uploads media to S3 and advances the wizard.
                      ? (isFinalForNewIteration ? 'Continue to BOQ' : 'Upload Updated Design')
                      : 'Save Design'
                )}
              </PrimaryButton>
            </div>
          )}
        </div>
    </FormModal>
  );
};

export default DesignSubmissionModal;
